// src/lib/supabase/queries/chat-session-queries.ts
/**
 * .tuMetrics {"tu1": 0.86, "tu2": 0.88, "tu3": 4, "notes": "Chat session row mapping and participant-match lookup helpers", "tuVersion": "2.5"}
 * .help "Maps chat_sessions DB rows and finds or creates matching stream sessions."
 */
import { supabase } from '../../supabase';
import { ChatSession } from '../../../types'; // Import ChatSession from index.ts
import type { Database } from '../../../types/supabase';

type ChatSessionRow = Database['public']['Tables']['chat_sessions']['Row'];
type ChatSessionInsert = Database['public']['Tables']['chat_sessions']['Insert'];

export const mapChatSessionFromDB = (row: ChatSessionRow): ChatSession => ({
  id: row.id,
  companyId: row.company_id,
  streamType: row.stream_type as ChatSession['streamType'],
  recipientStreamId: row.recipient_stream_id,
  startTime: row.start_time,
  endTime: row.end_time,
  lastActivityAt: row.last_activity_at,
  name: row.name || undefined, // User-provided session name
  summary: row.summary || undefined, // AI-generated summary
  createdBy: row.created_by || undefined,
  userIds: row.user_ids || [],
  agentIds: row.agent_ids || [],
  relatedCardId: row.related_card_id || undefined,
  relatedSprintId: row.related_sprint_id || undefined,
  relatedRoadmapId: row.related_roadmap_id || undefined,
  relatedRequirementId: row.related_requirement_id || undefined,
  createdAt: row.created_at,
  isStreamEnded: row.is_stream_ended,
  projectId: row.project_id ?? null,
  meetingOptions: row.meeting_options ?? null,
});

export const insertChatSession = async (sessionData: ChatSessionInsert) => {
  const { data, error } = await supabase
    .from('chat_sessions')
    .insert(sessionData)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const findExistingStreamByParticipants = async (
  companyId: string,
  streamType: ChatSession['streamType'],
  userIds: string[],
  agentIds: string[]
): Promise<ChatSession | null> => {
  // Find ANY session (even ended ones) with the same participants
  // This allows us to reuse recipient_stream_id for the same conversation stream
  // Fetch all sessions for this stream type and filter in JavaScript for exact participant match
  const { data, error } = await supabase
    .from('chat_sessions')
    .select(`
      id,
      company_id,
      stream_type,
      recipient_stream_id,
      start_time,
      end_time,
      last_activity_at,
      name,
      summary,
      created_by,
      user_ids,
      agent_ids,
      related_card_id,
      related_sprint_id,
      related_roadmap_id,
      related_requirement_id,
      created_at,
      is_stream_ended,
      project_id,
      meeting_options
    `)
    .eq('company_id', companyId)
    .eq('stream_type', streamType)
    .order('last_activity_at', { ascending: false });

  if (error) {
    console.error('Error finding existing stream:', error);
    return null;
  }

  if (!data || data.length === 0) return null;

  // Filter for exact participant match
  const matchingSession = data.find(session => {
    const sessionUserIds = session.user_ids || [];
    const sessionAgentIds = session.agent_ids || [];
    
    // Check exact match: same length and all IDs match
    const matchesUsers = sessionUserIds.length === userIds.length &&
      sessionUserIds.every((id: string) => userIds.includes(id)) &&
      userIds.every((id: string) => sessionUserIds.includes(id));
    
    const matchesAgents = sessionAgentIds.length === agentIds.length &&
      sessionAgentIds.every((id: string) => agentIds.includes(id)) &&
      agentIds.every((id: string) => sessionAgentIds.includes(id));
    
    return matchesUsers && matchesAgents;
  });

  if (matchingSession) {
    return mapChatSessionFromDB(matchingSession);
  }

  return null;
};

export const fetchChatSessionById = async (sessionId: string): Promise<ChatSession | null> => {
  const { data, error } = await supabase
    .from('chat_sessions')
    .select(`
      id,
      company_id,
      stream_type,
      recipient_stream_id,
      start_time,
      end_time,
      last_activity_at,
      name,
      summary,
      created_by,
      user_ids,
      agent_ids,
      related_card_id,
      related_sprint_id,
      related_roadmap_id,
      related_requirement_id,
      created_at,
      is_stream_ended,
      project_id,
      meeting_options
    `)
    .eq('id', sessionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  
  return data ? mapChatSessionFromDB(data) : null;
};

export const fetchChatSessions = async (companyId: string, streamType?: ChatSession['streamType'], limit?: number) => {
  let query = supabase
    .from('chat_sessions')
    .select(`
      id,
      company_id,
      stream_type,
      recipient_stream_id,
      start_time,
      end_time,
      last_activity_at,
      name,
      summary,
      created_by,
      user_ids,
      agent_ids,
      related_card_id,
      related_sprint_id,
      related_roadmap_id,
      related_requirement_id,
      created_at,
      is_stream_ended,
      project_id,
      meeting_options
    `)
    .eq('company_id', companyId);
    
  if (streamType) {
    query = query.eq('stream_type', streamType);
  }

  // Order by last_activity_at DESC, but handle NULLs by using start_time as fallback
  // We'll do the NULL handling in JavaScript after fetching
  query = query.order('last_activity_at', { ascending: false });
  
  if (limit) {
    query = query.limit(limit * 2); // Fetch more to account for NULL handling
  }

  const { data, error } = await query;

  if (error) throw error;
  
  const mapped = data.map(mapChatSessionFromDB);
  
  // Sort in JavaScript to properly handle NULLs: NULL last_activity_at falls back to start_time
  mapped.sort((a, b) => {
    const aTime = new Date(a.lastActivityAt || a.startTime || 0).getTime();
    const bTime = new Date(b.lastActivityAt || b.startTime || 0).getTime();
    return bTime - aTime;
  });
  
  // Apply limit after sorting
  return limit ? mapped.slice(0, limit) : mapped;
};

/**
 * Update the last_activity_at timestamp for a chat session
 * Called when a new message is sent/received in a stream
 */
export const updateChatSessionActivity = async (
  recipientStreamId: string,
  streamType: ChatSession['streamType'],
  companyId: string
): Promise<void> => {
  const now = new Date().toISOString();
  
  console.log('📝 [updateChatSessionActivity] Updating session:', {
    recipientStreamId,
    streamType,
    companyId,
    timestamp: now,
    timestampDate: new Date(now).toLocaleString()
  });
  
  const { data, error } = await supabase
    .from('chat_sessions')
    .update({ last_activity_at: now })
    .eq('recipient_stream_id', recipientStreamId)
    .eq('stream_type', streamType)
    .eq('company_id', companyId)
    .eq('is_stream_ended', false) // Only update active sessions
    .select();
  
  if (error) {
    console.error('❌ [updateChatSessionActivity] Error:', error);
    // Don't throw - this is a non-critical update
  } else {
    console.log('✅ [updateChatSessionActivity] Updated sessions:', {
      updatedCount: data?.length || 0,
      sessions: data
    });
  }
};
